import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api.js';

// This is the full list of all possible roles in the system.
const allUserRoles = [
  'Super Admin',
  'Admin',
  'Verifier',
  'Collector',
  'Payment Redeemer',
  'Payment Poster',
  'Hearing Representative',
  'Provider'
];

const RegisterUserForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    role: 'Collector', // Default role
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mailStatus, setMailStatus] = useState({ sending: false, message: '' });

  // Assuming useAuth() provides the currently logged-in user object.
  const { register, user } = useAuth();

  // Filter the roles that can be assigned based on the current user's role.
  const availableRoles = useMemo(() => {
    if (user && user.role === 'Admin') {
      return allUserRoles.filter(role => role !== 'Super Admin');
    }
    return allUserRoles;
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendWelcomeEmail = async ({ email, username,password,fullName }) => {
    try {
      setMailStatus({ sending: true, message: '' });
      await api.post('/emails/welcome', {
        to: email,
        username,
        password,
        fullName,
      });
      setMailStatus({ sending: false, message: 'Welcome email sent.' });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Welcome email failed to send.';
      setMailStatus({ sending: false, message: msg });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setMailStatus({ sending: false, message: '' });

    // Keep a copy before we reset the form
    const payload = { ...formData };

    const result = await register(payload);
    if (result.success) {
      setSuccess('User registered successfully!');
      // Fire-and-forget welcome email (reports its own status in UI)
      sendWelcomeEmail({
        email: payload.email,
        username: payload.username,
        password: payload.password,
        fullName: payload.fullName,
      });

      // Reset form
      setFormData({
        fullName: '',
        username: '',
        email: '',
        password: '',
        role: 'Collector',
      });
    } else {
      setError(result.message || 'Failed to register user.');
    }
  };

  return (
    <div className="p-8 bg-white rounded-lg  w-full max-w-2xl mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Create a New User Account</h2>
      <form onSubmit={handleSubmit}>
        {error && (
          <p className="text-red-700 bg-red-100 border border-red-200 p-3 rounded-md mb-4">
            {error}
          </p>
        )}
        {success && (
          <p className="text-green-800 bg-green-100 border border-green-200 p-3 rounded-md mb-4">
            {success}
          </p>
        )}
        {!!mailStatus.message && (
          <p className={`p-3 rounded-md mb-4 border ${
            mailStatus.message.includes('failed')
              ? 'text-yellow-900 bg-yellow-100 border-yellow-200'
              : 'text-blue-900 bg-blue-100 border-blue-200'
          }`}>
            {mailStatus.sending ? 'Sending welcome email…' : mailStatus.message}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fullName">Full Name</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="fullName"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">Username</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">Email</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">Password</label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="password"
              type="password"
              name="password"
              value={formData.password}
              minLength="6"
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-6 md:col-span-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">Role</label>
            <select
              name="role"
              id="role"
              onChange={handleChange}
              value={formData.role}
              className="shadow border rounded w-full py-2 px-3 text-gray-700"
            >
              {availableRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-center mt-4">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline disabled:opacity-60"
            type="submit"
            disabled={mailStatus.sending}
          >
            {mailStatus.sending ? 'Registering…' : 'Register User'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterUserForm;
